import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Link,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Field, Form } from 'react-final-form';
import {
  buildWelcomeUrl,
  encodeFreeTrialPlanState,
} from '../../../libs/auth/freeTrialOffice';
import { loadAuth0WebAuth, prefetchAuth0WebAuth } from '../../../libs/auth/loadAuth0WebAuth';
import phoneNumberIsValid, { required } from '../../../libs/forms/forms-validation';
import { formatPhoneNumber, formatPhoneInput } from '../../../libs/helpers/normalizePhoneNumber';
import InputChakra from '../../FormElements/InputChakra/InputChakra';
import PrimaryButton from '../../Shared/Buttons/PrimaryButton';

const companySizeOptions = [
  { label: '1-4 Appraisers', value: '1-4' },
  { label: '5-19 Appraisers', value: '5-19' },
  { label: '20-49 Appraisers', value: '20-49' },
  { label: '50+ Appraisers', value: '50+' },
];

function CompanySizeField({ input, meta: { error, touched }, label }) {
  return (
    <FormControl isInvalid={Boolean(error && touched)} mb={{ base: '2', md: '3' }}>
      <FormLabel htmlFor={input.name}>{label}</FormLabel>
      <SimpleGrid columns={{ base: 1, sm: 2 }} gap={3}>
        {companySizeOptions.map((option) => {
          const isSelected = input.value === option.value;
          return (
            <Button
              key={option.value}
              type="button"
              variant="outline"
              height="48px"
              borderColor={isSelected ? 'brand.green' : 'gray.500'}
              borderWidth={isSelected ? '2px' : '1px'}
              color={isSelected ? 'green.900' : 'gray.700'}
              bg={isSelected ? 'green.100' : 'brand.white'}
              fontSize="sm"
              fontWeight="400"
              textTransform="none"
              onClick={() => input.onChange(option.value)}
            >
              {option.label}
            </Button>
          );
        })}
      </SimpleGrid>
      {error && touched && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
}

CompanySizeField.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  meta: PropTypes.shape({
    error: PropTypes.string,
    touched: PropTypes.bool,
  }).isRequired,
  label: PropTypes.string,
};

CompanySizeField.defaultProps = {
  label: 'Team size',
};

const validatePhone = (value) => {
  if (!value) return 'Required';
  if (!formatPhoneNumber(value) && !phoneNumberIsValid(value)) {
    return 'Enter a valid phone number';
  }
  return undefined;
};

const validatePassword = (value) => {
  if (!value) return 'Required';
  if (value.length < 8) return 'Must be at least 8 characters';
  return undefined;
};

const validateEmail = (value) => {
  if (!value) return 'Required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email';
  return undefined;
};

export default function FreeTrialForm({
  auth0Browser,
  referral,
  requestedPlan,
}) {
  const hasPlan = typeof requestedPlan === 'string';
  const [serverError, setServerError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [accountExists, setAccountExists] = useState(false);
  // react-final-form clears `submitting` as soon as the handler resolves, which
  // happens before the browser leaves the page. Track the navigation separately
  // so the button never becomes clickable again after a successful signup.
  const [navigating, setNavigating] = useState(false);
  const submittingRef = useRef(false);
  const navigatedRef = useRef(false);
  const emailCheckRef = useRef('');

  const beginNavigation = useCallback(() => {
    navigatedRef.current = true;
    setNavigating(true);
  }, []);

  useEffect(() => {
    prefetchAuth0WebAuth();
  }, []);

  const checkEmail = useCallback(async (email) => {
    const normalized = (email || '').trim().toLowerCase();
    emailCheckRef.current = normalized;
    if (!normalized || !normalized.includes('@')) return;
    try {
      const response = await fetch('/api/onboarding/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalized }),
      });
      const body = await response.json().catch(() => ({}));
      if (emailCheckRef.current !== normalized) return;
      if (body.available === false) {
        setAccountExists(true);
        setFieldErrors((prev) => ({
          ...prev,
          email: 'An account with this email already exists.',
        }));
      } else if (body.available === true) {
        setAccountExists(false);
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next.email;
          return next;
        });
      }
    } catch {
      // Unavailable — complete-signup re-checks on submit.
    }
  }, []);

  const handleSubmit = async (values) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setServerError(null);
    setFieldErrors({});

    try {
      if (
        !auth0Browser?.clientId
        || !auth0Browser?.domain
        || !auth0Browser?.redirectUri
      ) {
        throw new Error('Browser authentication is not configured.');
      }

      const webAuthReady = loadAuth0WebAuth();

      const response = await fetch('/api/onboarding/complete-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...values,
          email: (values.email || '').toLowerCase(),
          referral,
          requestedPlan,
          termsAccepted: Boolean(values.termsAccepted),
        }),
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (body.field) {
          setFieldErrors({ [body.field]: body.detail });
        }
        if (body.code === 'email_taken') {
          setAccountExists(true);
          setFieldErrors((prev) => ({
            ...prev,
            email: body.detail || 'An account with this email already exists.',
          }));
        }
        if (body.code === 'register_incomplete') {
          if (body.redirectTo) {
            beginNavigation();
            window.location.href = body.redirectTo;
          } else {
            setServerError(body.detail || 'Signup could not be completed.');
          }
          return;
        }
        setServerError(body.detail || 'Signup failed. Please try again.');
        return;
      }

      beginNavigation();
      const welcomeUrl = buildWelcomeUrl({
        ssoFailed: true,
        ...(typeof requestedPlan === 'string' ? { plan: requestedPlan } : {}),
      });
      try {
        const WebAuth = await webAuthReady;
        if (!WebAuth) {
          throw new Error('Browser authentication could not be loaded.');
        }

        const webAuth = new WebAuth({
          audience: auth0Browser.audience,
          clientID: auth0Browser.clientId,
          domain: auth0Browser.domain,
          redirectUri: auth0Browser.redirectUri,
          responseType: 'code',
          scope: 'openid profile email',
        });

        const planState = encodeFreeTrialPlanState(requestedPlan);
        webAuth.login({
          password: values.password,
          realm: auth0Browser.connection,
          username: values.email.toLowerCase(),
          ...(planState ? { state: planState } : {}),
        }, () => {
          window.location.assign(welcomeUrl);
        });
      } catch {
        window.location.assign(welcomeUrl);
      }
    } catch (error) {
      setServerError(error.message || 'Signup failed. Please try again.');
    } finally {
      if (!navigatedRef.current) {
        submittingRef.current = false;
        setNavigating(false);
      }
    }
  };

  const initialValues = useMemo(() => ({
    companyName: '',
    companySize: '',
    name: { first: '', last: '' },
    email: '',
    phone: '',
    password: '',
    termsAccepted: false,
  }), []);

  return (
    <Box w="100%">
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit: submit, submitting }) => (
          <Box as="form" onSubmit={submit} maxW="520px" w="100%">
            <SimpleGrid columns={{ base: 1, sm: 2 }} gap={3}>
              <Field
                label="First name"
                name="name.first"
                component={InputChakra}
                placeholder="First name"
                validate={required}
              />
              <Field
                label="Last name"
                name="name.last"
                component={InputChakra}
                placeholder="Last name"
                validate={required}
              />
            </SimpleGrid>
            <Field
              name="email"
              validate={validateEmail}
              parse={(value) => (value || '').toLowerCase()}
            >
              {({ input, meta }) => (
                <InputChakra
                  label="Work email"
                  placeholder="you@company.com"
                  input={{
                    ...input,
                    onChange: (event) => {
                      input.onChange(event);
                      emailCheckRef.current = (event.target.value || '').trim().toLowerCase();
                      setAccountExists(false);
                      setFieldErrors((prev) => {
                        if (!prev.email) {
                          return prev;
                        }
                        const next = { ...prev };
                        delete next.email;
                        return next;
                      });
                    },
                    onBlur: (event) => {
                      input.onBlur(event);
                      checkEmail(event.target.value);
                    },
                  }}
                  meta={meta}
                  formHelperText={hasPlan ? 'You’ll use this to log in.' : null}
                  additionalProps={{ textTransform: 'lowercase' }}
                />
              )}
            </Field>
            {fieldErrors.email && (
            <Text fontSize="sm" color="brand.red" mt={-2} mb={3}>
              {fieldErrors.email}
              {' '}
              <Link href="/auth/login" color="brand.green" fontWeight="600">Log in</Link>
            </Text>
            )}
            <Field
              label="Company"
              name="companyName"
              component={InputChakra}
              placeholder="Company name"
              validate={required}
            />
            <Field
              type="tel"
              label="Phone number"
              name="phone"
              component={InputChakra}
              placeholder="(555) 555-5555"
              validate={validatePhone}
              format={formatPhoneInput}
              parse={formatPhoneInput}
              formHelperText={hasPlan ? 'In case we need to reach you about your account.' : null}
              additionalProps={{ inputMode: 'tel', autoComplete: 'tel' }}
            />
            {fieldErrors.phone && (
            <Text fontSize="sm" color="brand.red" mt={-2} mb={3}>{fieldErrors.phone}</Text>
            )}
            <Field
              type="password"
              label="Password"
              name="password"
              component={InputChakra}
              placeholder="Create a password"
              validate={validatePassword}
            />
            {fieldErrors.password && (
            <Text fontSize="sm" color="brand.red" mt={-2} mb={3}>{fieldErrors.password}</Text>
            )}
            <Field
              name="companySize"
              component={CompanySizeField}
              label="How many appraisers are on your team?"
              validate={required}
            />
            <Field name="termsAccepted" type="checkbox" validate={(value) => (value ? undefined : 'Required')}>
              {({ input, meta }) => (
                <FormControl isInvalid={Boolean(meta.error && meta.touched)} mb={4}>
                  <Checkbox
                    {...input}
                    isChecked={Boolean(input.checked)}
                    colorScheme="green"
                    alignItems="flex-start"
                  >
                    <Text fontSize="sm" color="gray.700">
                      I agree to the
                      {' '}
                      <Link href="https://anow.com/terms-conditions/" isExternal color="brand.green">
                        Terms
                      </Link>
                      {' '}
                      and
                      {' '}
                      <Link href="https://anow.com/privacy-policy/" isExternal color="brand.green">
                        Privacy Policy
                      </Link>
                      .
                    </Text>
                  </Checkbox>
                  {meta.error && meta.touched && (
                  <FormErrorMessage>{meta.error}</FormErrorMessage>
                  )}
                </FormControl>
              )}
            </Field>
            {serverError && (
            <Text fontSize="sm" color="brand.red" mb={3}>{serverError}</Text>
            )}
            <PrimaryButton
              type="submit"
              arrow="right-white"
              isLoading={submitting || navigating}
              isDisabled={submitting || navigating}
              loadingText={navigating ? 'Setting up your account' : 'Creating your account'}
              width={{ base: '100%', md: 'initial' }}
              height="46px"
              px={6}
              borderRadius="md"
            >
              {hasPlan ? 'Continue to payment' : 'Start your 30 day free trial'}
            </PrimaryButton>
            {hasPlan && (
            <Text fontSize="sm" color="gray.600" mt={3}>
              Next: secure payment. You won’t be charged until you confirm your order.
            </Text>
            )}
            {(!hasPlan || accountExists) && (
            <Stack mt={4} spacing={1}>
              <Text fontSize="sm" color="gray.600">
                Already have an account?
                {' '}
                <Link href="/auth/login" color="brand.green" fontWeight="600">Log in</Link>
              </Text>
            </Stack>
            )}
          </Box>
        )}
      />
    </Box>
  );
}

FreeTrialForm.propTypes = {
  auth0Browser: PropTypes.shape({
    audience: PropTypes.string,
    clientId: PropTypes.string,
    connection: PropTypes.string,
    domain: PropTypes.string,
    redirectUri: PropTypes.string,
  }),
  referral: PropTypes.string,
  requestedPlan: PropTypes.string,
};

FreeTrialForm.defaultProps = {
  auth0Browser: null,
  referral: null,
  requestedPlan: null,
};
